/**
 * The location of technical indicator data on a tech chart.
 */
export enum TechChartChartLocation {
    // Displayed on a y-axis separate from ohlc data
    indicator = 'indicator',
    // Displayed on the same y-axis as the ohlc data
    overlay = 'overlay',
}

/**
 * Options that apply to the chart as a whole
 */
export enum ChartOptions {
    ADJUSTED = 'adjusted',
    SHOW_US_RECESSIONS = 'recessions',
    USE_LOG_SCALE = 'scaleType',
    NAME_IN_LEGEND = 'nameInLegend',
    SHOW_UNITS_IN_LEGEND = 'units',
    QUOTE_LEGEND = 'quoteLegend',
    LEGEND_ON_CHART = 'legendOnChart',
    DISPLAY_DATE_RANGE = 'displayDateRange',
    HIDE_ZOOM_BAR = 'hideZoomBar',
    SHOW_WATERMARK = 'showWatermark',
    SHOW_PERFORMANCE_DISCLOSURE = 'performanceDisclosure',
}
/**
 * Technical Chart Style
 */
export enum ChartStyle {
    // Display OHLC as candlestick chart
    CANDLESTICK = 'candlestick',
    // Display closing price
    LINE = 'line',
    // Display OHLC as bar chart
    OHLC = 'ohlc',
}
/**
 * Options for scale type
 */
export enum TechChartScaleType {
    LINEAR = 'linear',
    LOG = 'log',
}

export enum ChartType {
    // Legend and tooltips update according to the user's mouse location
    INTERACTIVE = 'interactive',
    // Static Image
    PRESENTATION = 'presentation',
}

// TODO: Rename to TechChartFrequency
/**
 * Options that apply to frequency
 * Please note: We also may need to add additional options depending on
 * where we land on presenting intraday data and potentially doing
 * things like 1 hour, etc.
 */
export enum Frequency {
    DAILY = 'market_daily',
    WEEKLY = 'weekly',
    MONTHLY = 'monthly',
}

/**
 * How much tech chart data is displayed
 */
export enum TechChartDataAmount {
    DAILY = 'daily',
    SINGLE_DAY_INTRADAY = 'single_day_intraday',
    MULTI_DAY_INTRADAY = 'multi_day_intraday',
    MULTI_WEEK = 'multi_week',
    MULTI_MONTH = 'multi_month',
    MULTI_YEAR = 'multi_year',
}

/**
 * Types of securities whose data can be charted in a technical chart
 */
export enum TechChartSecurityType {
    company = 'company',
    index = 'index',
}

/**
 * Names of Technical Indicators
 *
 * Used by the backend to identify technical indicators.
 * These are similar to (but not the same as!) TechChartDataSeriesType.
 */
export enum TechnicalType {
    AROON_OSCILLATOR = 'aroon_oscillator',
    AROON = 'aroon',
    ATR = 'atr',
    CCI = 'cci',
    CMF = 'cmf',
    DMI = 'dmi',
    ROC = 'roc',
    STOCHASTIC_FULL = 'stochastic_full',
    STOCHASTIC_SLOW = 'stochastic_slow',
    STOCHASTIC_FAST = 'stochastic_fast',
    WILLIAMS_R = 'williams_r',
    SMA = 'sma',
    EMA = 'ema',
    MA_OSCILLATOR = 'ma_oscillator',
    BOLLINGER = 'bollinger',
    VOLUME = 'volume',
    AVERAGE_VOLUME = 'average_volume',
    RSI = 'rsi',
    MACD = 'macd',
    ROLLING_VOL = 'rolling_vol',
    MOMENTUM_OSCILLATOR = 'momentum_oscillator',
    ICHIMOKU = 'ichimoku',
    MA_ENVELOPES = 'ma_envelopes',
    KELTNER_CHANNELS = 'keltner_channels',
    CHANDELIER_EXIT_LONG = 'chandelier_exit_long',
    CHANDELIER_EXIT_SHORT = 'chandelier_exit_short',
    STOCHASTIC_RSI = 'stochastic_rsi',
    TRIX = 'trix',
    OBV = 'obv',
}

export enum TechnicalCategory {
    OVERLAY = 'overlay',
    OSCILLATOR = 'oscillator',
    OTHER = 'other',
}

/**
 * Names used by Highstock to identify technical indicators.
 * These values can be assigned to Highstock.Options.series.type.
 */
export enum TechChartDataSeriesType {
    // OHLC
    hollowcandlestick = 'hollowcandlestick',
    ohlc = 'ohlc',
    line = 'line',
    // Technicals
    aroonoscillator = 'aroonoscillator',
    aroon = 'aroon',
    atr = 'atr',
    cci = 'cci',
    cmf = 'cmf',
    dmi = 'dmi',
    roc = 'roc',
    stochastic = 'stochastic',
    slowstochastic = 'slowstochastic',
    williamsr = 'williamsr',
    sma = 'sma',
    ema = 'ema',
    bb = 'bb',
    volume = 'volume',
    rsi = 'rsi',
    macd = 'macd',
    momentum = 'momentum',
    ikh = 'ikh',
    priceenvelopes = 'priceenvelopes',
    keltnerchannels = 'keltnerchannels',
    trix = 'trix',
    obv = 'obv',
}

/**
 * Formats for displaying data in the legend.
 */
export enum TechChartLegendItemDisplayStyle {
    // Display the OHLC values
    OHLC = 'ohlc',
    // Display only the close value
    OHLC_LINE = 'ohlc_line',
    // Display the calculated value
    TECHNICAL = 'technical',
    // Display an array of multiple value types
    MULTI_VALUE = 'multi_value',
}

export enum XOHLCDataIndex {
    'X_VAL' = 0,
    'OPEN' = 1,
    'HIGH' = 2,
    'LOW' = 3,
    'CLOSE' = 4,
}

export enum TechnicalCalcDataIndex {
    'X_VAL' = 0,
    'CALC' = 1,
}

export enum VolumeDataIndex {
    'X_VAL' = 0,
    'VOLUME' = 1,
}

export enum TechnicalKey {
    'TYPE' = 'type',
    'PARAMS' = 'params',
    'INCLUDE' = 'include',
    'COLOR' = 'color',
}

export enum TechChartSecurityKey {
    'COLOR' = 'color',
    'ID' = 'id',
}

export enum TechChartDateSelection {
    'RANGE' = 'range',
    'YEAR' = 'year',
}

export enum TechChartStatus {
    ok = 'ok',
    error = 'error',
    loading = 'loading',
    noData = 'noData',
    getStarted = 'getStarted',
}

export enum TechChartPresentationValidModes {
    imageCode = 'image_code',
    imageCodeTemplate = 'image_code_template',
    imageContents = 'image_contents',
    imageDownload = 'image_download',
    imageAttachment = 'image_attachment',
    imageUrl = 'image_url',
}

export enum TechChartAnnotationName {
    // text annotation
    TEXT = 'Text',

    // shape annotations
    CIRCLE = 'Circle',
    RECTANGLE = 'Rectangle',

    // line annotations
    TREND_SEGMENT = 'Trend Segment',
    ARROW_SEGMENT = 'Arrow Segment',
    RAY = 'Ray',
    ARROW_RAY = 'Arrow Ray',
    LINE = 'Line',
    ARROW_LINE = 'Arrow Line',
    HORIZONTAL_LINE = 'Horizontal Line',
    VERTICAL_LINE = 'Vertical Line',

    // Counter label annotations
    LABEL_COUNTER = 'Label Counter',
    VERTICAL_COUNTER = 'Vertical Counter',
    ARROW_COUNTER = 'Arrow Counter',

    // Cursor annotations
    CURSOR = 'cursor',
    CROSSHAIR = 'crosshair',

    // Crooked annotations
    CROOKED_LINE_3 = 'Crooked Line 3',
    CROOKED_LINE_5 = 'Crooked Line 5',
    ELLIOTT_3 = 'Elliott 3',
    ELLIOTT_5 = ' Elliott 5',

    // Tool measure annotations
    MEASURE_XY = 'Measure XY',
    MEASURE_X = 'Measure X',
    MEASURE_Y = 'Measure Y',

    // Tool adv annotations
    PITCHFORK = 'Pitchfork',
    PARALLEL_CHANNEL = 'Parallel Channel',
    FIBONACCI = 'Fibonacci',
}

// Cursor annotations
export enum TechChartCursorType {
    CURSOR = 'cursor',
    CROSSHAIR = 'crosshair',
}
